import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import TextField from "@/components/TextField";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useJobPostReviewRequestEmailPreview } from "../_hooks/useJobPostReviewRequestEmailPreview";

export default function SendJobPostReviewModal({
  disabled,
  open,
  setOpen,
  setJobPostReviewRequestVariables,
  sendJobPostReviewRequest,
  jobPostReviewRequestVariables
}: {
  disabled: boolean;
  open: boolean;
  setOpen: (value: boolean) => void;
  setJobPostReviewRequestVariables: (value: any) => void;
  sendJobPostReviewRequest: () => void;
  jobPostReviewRequestVariables: { id: string; notes?: string };
}) {
  const { control, handleSubmit, setValue } = useForm();
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useJobPostReviewRequestEmailPreview(jobPostReviewRequestVariables.id);

  useEffect(() => {
    if (!open) {
      setValue("reviewNotes", "");
      setPreviewOpen(false);
      resetPreview();
    }
  }, [open, resetPreview, setValue]);

  const handlePreviewEmail = () => {
    setPreviewOpen(true);
    void loadPreview(jobPostReviewRequestVariables.notes);
  };

  return (
    <>
      <Dialog
        open={open}
        onClosed={() => {
          setOpen(false);
        }}
        headline="Send for Review"
        className={`${EMAIL_PREVIEW_MODAL_CLASSNAME} p-6 pr-2 max-h-[80vh]`}
        actions={
          <EmailPreviewModalActions onPreview={handlePreviewEmail} previewDisabled={disabled}>
            <Button
              variant="text"
              onClick={() => {
                setJobPostReviewRequestVariables({ ...jobPostReviewRequestVariables, notes: "" });
                setValue("reviewNotes", "");
                setOpen(false);
              }}
              disabled={disabled}
            >
              Cancel
            </Button>
            <Button
              trailingIcon={false}
              icon={disabled ? "progress_activity" : "ios_share"}
              iconClassName={classNames(disabled && "animate-spin w-6 h-6 !block")}
              onClick={handleSubmit(() => sendJobPostReviewRequest())}
              disabled={disabled}
            >
              Send for Review
            </Button>
          </EmailPreviewModalActions>
        }
        hasFormElements
      >
        <div className="overflow-auto -mt-2 space-y-6">
          <div>
            Sending a job post for review sends an email to the Employer that includes a link to this job post on the
            job board. You may also include an optional note for context. The Employer will need to review the job,
            accept terms and conditions, and publish the job to make it visible to potential applicants.
          </div>

          <Controller
            name="reviewNotes"
            control={control}
            render={({ field: { onChange, value }, fieldState }) => (
              <TextField
                onChangeText={(text) => {
                  onChange(text);
                  setJobPostReviewRequestVariables({ ...jobPostReviewRequestVariables, notes: text });
                }}
                value={value}
                label="Recipient Note"
                type="textarea"
                rows={6}
                className="w-full"
                error={fieldState.invalid}
                errorText={fieldState.error?.message}
              />
            )}
          />
        </div>
      </Dialog>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
}
